import React, { useLayoutEffect, useRef } from "react";
import useCharidyCampaign from "../../hooks/useCharidyCampaign";

const formatAmount = (n) =>
  typeof n === "number" ? n.toLocaleString("en-US") : n;

const tickerLabel = (d) => {
  let label = d.name;
  if (!d.hideAmount && d.amount) {
    label += ` - ${d.currencySign}${formatAmount(d.amount)}`;
  }
  if (d.dedication) {
    label += ` - ${d.dedication}`;
  }
  return label;
};

const SCRIPT_FONT = "'Caveat', 'Segoe Script', 'Comic Sans MS', cursive";

function CampaignBanner({ config }) {
  const { donations, donorCount } = useCharidyCampaign(
    config.campaign_shortlink
  );

  const bannerRef = useRef(null);

  // The site header is position:fixed (App.css #main-header). The banner is
  // fixed above it, and this measurement pushes the header + page content
  // down by the banner's real height (it wraps at small widths).
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) return undefined;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--campaign-banner-h",
        `${el.offsetHeight}px`
      );
    apply();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      document.documentElement.style.removeProperty("--campaign-banner-h");
    };
  }, []);

  const donorGoal = Number(config.donor_goal) > 0 ? Number(config.donor_goal) : 1000;
  const progressPct =
    donorCount != null ? Math.min(100, (donorCount / donorGoal) * 100) : 0;

  return (
    <section id="campaign-banner" ref={bannerRef} className="bg-[#F9F6EE] text-[#7B1F1F]">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-y-2 px-4 py-2 md:flex-row md:flex-wrap md:justify-between md:gap-x-8 md:gap-y-3 md:py-3 xl:px-8">
        <a
          href={config.donate_url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 -rotate-3 whitespace-nowrap text-2xl leading-none text-[#A63A3A] hover:text-[#7B1F1F] md:text-3xl"
          style={{ fontFamily: SCRIPT_FONT, fontWeight: 600 }}
        >
          Please help InsideChassidus.org
        </a>
        <a
          href={config.donate_url}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden text-center text-lg font-extrabold leading-tight hover:underline md:block"
        >
          Join InsideChassidus.org&apos;s
          <br />
          Chai Ellul Campaign!
        </a>
        <div className="flex w-full items-center justify-between gap-4 md:w-auto md:justify-start md:gap-5">
          {donorCount != null && (
            <div className="w-44 text-center md:w-48">
              <p className="text-sm font-bold">
                {formatAmount(donorCount)} partners have joined
              </p>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={donorGoal}
                aria-valuenow={Math.min(donorCount, donorGoal)}
                aria-label="Campaign partners"
                className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#7B1F1F]/20"
              >
                <div
                  className="h-full rounded-full bg-[#7B1F1F]"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <a
                href={config.donate_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 hidden text-sm font-bold underline md:inline-block"
              >
                Join Now!
              </a>
            </div>
          )}
          <a
            href={config.donate_url}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap rounded bg-[#d9534f] px-5 py-2 text-base font-bold text-white transition-colors hover:bg-[#c9302c] md:px-6 md:text-lg"
          >
            Please Donate
          </a>
        </div>
      </div>
      {donations.length > 0 && (
        <div className="border-t border-[#7B1F1F]/20 bg-[#7B1F1F]/5">
          <div className="mx-auto flex max-w-7xl items-center px-4 xl:px-8">
            <span className="shrink-0 py-1.5 pr-4 text-sm font-semibold">
              Thank you:
            </span>
            <div className="campaign-ticker overflow-hidden">
              <div className="campaign-ticker-track flex w-max whitespace-nowrap py-1.5">
                {[0, 1].map((copy) => (
                  <div key={copy} aria-hidden={copy === 1} className="flex shrink-0">
                    {donations.map((d) => (
                      <span key={`${copy}-${d.id}`} className="px-4 text-sm">
                        {tickerLabel(d)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CampaignBanner;
